import React, { useContext, useEffect, useReducer, useRef } from 'react';
import { RouteProps, withRouter } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import BulkActionProgressModal from 'Components/BulkActionProgressModal';
import { isAsyncViewLoadedState } from 'Lib/react-utils';
import { Notification, RootProps } from 'Lib/types';
import isPositionStickySupported from 'Lib/utils/isPositionStickySupported';
import logger from 'Lib/utils/logger';
import env from 'Src/env';

import {
  Activities,
  ErrorBoundary,
  MeContext,
  ToastContext,
  ToastManager,
} from './components';
import Footer from './components/Footer';
import SidebarNavigation from './components/SidebarNavigation';
import useRedirectToLegacy from './lib/useRedirectToLegacy/useRedirectToLegacy';
import Main from './Main';

import 'normalize.css';
import 'react-select-legacy/dist/react-select.css';
import 'flatiron/static/antd.css';
import 'fonturn/css/style.css';
import 'Lib/styles/root.css';

type Props = RouteProps &
  RootProps & {
    hasInventoryLinesheet: boolean;
  };

enum RefName {
  globalNotificationsRef = 'globalNotificationsRef',
  messengerManagerRef = 'messengerManagerRef',
  navigationRef = 'navigationRef',
}

// [CLARITY REMOVED] - Microsoft Clarity has been removed from INTURN entirely.
// declare global {
//   interface Window {
//     clarity: (
//       action: 'identify' | 'event' | 'stop',
//       userId?: string,
//       properties?: Record<string, any>,
//     ) => void;
//   }
// }

// [CLARITY REMOVED]
// function initializeClarity(clarityId: string): void {
//   (function (c: any, l: any, a: string, r: string, i: string) {
//     c[a] =
//       c[a] ||
//       function (...args: any[]) {
//         (c[a].q = c[a].q || []).push(args);
//       };
//     const t = l.createElement(r);
//     t.async = 1;
//     t.src = `https://www.clarity.ms/tag/${i}`;
//     const y = l.getElementsByTagName(r)[0];
//     if (y?.parentNode) {
//       y.parentNode.insertBefore(t, y);
//     } else {
//       l.head.appendChild(t);
//     }
//   })(window, document, 'clarity', 'script', clarityId);
// }

const Root = (props: Props) => {
  const { toast } = useContext(ToastContext);
  const { me } = useContext(MeContext);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const isAsyncViewLoaded = useRecoilValue(isAsyncViewLoadedState);

  useRedirectToLegacy({ pathName: props.location.pathname });

  logger.setContext({
    me,
    service: 'iui',
    environment: process.env.CI_ENVIRONMENT_NAME || process.env.DEPLOY_ENV,
  });

  useEffect(() => {
    if (localStorage.getItem('token')) {
      const disconnect = Activities.connect();
      return disconnect;
    }
  }, [localStorage.getItem('token')]);

  // [CLARITY REMOVED] - Microsoft Clarity removed from INTURN entirely.
  // GTM dataLayer pushes preserved for Google Tag Manager compatibility.
  useEffect(() => {
    if (!me.email || !window.dataLayer || !env.GTM_CONTAINER_ID) return;

    const email: string = me.email;

    const timer = setTimeout(() => {
      window.dataLayer.push({ username: email });
      window.dataLayer.push({ event: 'user-data-set' });
    }, 0);

    return () => clearTimeout(timer);
  }, [me.email]);

  const rootRefs: { [key in RefName]: any | null } = {
    globalNotificationsRef: useRef(null),
    messengerManagerRef: useRef(null),
    navigationRef: useRef(null),
  };

  const setRef = (name: RefName) => (ref: React.RefObject<HTMLElement>) => {
    rootRefs[name].current = ref;
  };

  const getRef = (name: RefName) => rootRefs[name].current;

  const activityProps = {
    subscribe: Activities.subscribe,
    unsubscribe: Activities.unsubscribe,
    unsubscribeById: Activities.unsubscribeById,
  };

  // NOTE: we use forceUpdate in these methods because Activities is not using state to manage notifications
  const processesProps = {
    getCurrentIndex: Activities.getCurrentIndex,
    push: (notification: Notification) => {
      const id = Activities.push(notification);
      forceUpdate(null);
      return id;
    },
    upsert: (notification: Notification) => {
      const id = Activities.upsert(notification);
      forceUpdate(null);
      return id;
    },
    update: (notification: Notification) => {
      const isUpdated = Activities.update(notification);
      forceUpdate(null);
      return isUpdated;
    },
    remove: (id: string) => {
      Activities.remove(id);
      forceUpdate(null);
    },
    has: Activities.has,
    indexOf: Activities.indexOf,
    get: Activities.notifications,
  };

  const openMessenger = (userId: string | null) => {
    if (getRef(RefName.messengerManagerRef)) {
      getRef(RefName.messengerManagerRef).open(userId);
    }
  };

  const closeMessenger = () => {
    if (getRef(RefName.messengerManagerRef)) {
      getRef(RefName.messengerManagerRef).onClose();
    }
  };

  const rootProps: RootProps = {
    ...props,
    notify: toast,
    activity: activityProps,
    process: processesProps,
    messenger: { open: openMessenger, close: closeMessenger },
  };

  const blockHeaderAndFooterRegex = isPositionStickySupported()
    ? /linesheet|pdp|inventory|auth|onboarding|verify|simpleBuyer/
    : /linesheet|pdp|auth|onboarding|verify|simpleBuyer/;

  const blockHeaderAndFooter =
    props.location.pathname === '/' ||
    blockHeaderAndFooterRegex.test(props.location.pathname);

  const hideSidebarNavigationRegex = /pdp|auth|onboarding|verify|simpleBuyer/;

  const hideSidebarNavigation =
    props.location.pathname === '/' ||
    hideSidebarNavigationRegex.test(props.location.pathname);

  const shouldShowSecondarySidebar = /linesheet|inventory\/(items|casepacks)/.test(
    props.location.pathname,
  );

  return (
    <>
      <SidebarNavigation
        pathname={props.location.pathname}
        {...rootProps}
        isHidden={hideSidebarNavigation}
        messenger={getRef(RefName.messengerManagerRef)}
        setRef={setRef(RefName.navigationRef)}
        shouldShowSecondarySidebar={shouldShowSecondarySidebar}
      >
        <Main {...rootProps} />
        {isAsyncViewLoaded && <Footer isHidden={blockHeaderAndFooter} />}
      </SidebarNavigation>
      {me.id && (
        <>
          <ErrorBoundary>
            <ToastManager />
          </ErrorBoundary>
        </>
      )}
      <ErrorBoundary>
        <BulkActionProgressModal activity={rootProps.activity} />
      </ErrorBoundary>
    </>
  );
};

// TODO: fix the RootProps
export default withRouter(Root as any);
